// テキスト図形の本文・書式を更新する op を、オブジェクトへ反映する共有ロジック。
//
// サーバ権威の op 適用（apps/api の op-applier）とクライアントの楽観適用（apps/web の
// canvas-doc-store）が同じ入力から同じオブジェクトを導出しなければならない。導出結果が
// 食い違うと、op を送った直後だけ書式や本文がずれ、サーバ確定で直る（＝原因を追いにくい）
// 挙動になるため、実装をこの 1 か所へ集約する。

import type { CanvasObject, TextCanvasObject } from "./canvas-object";
import type { UpdateTextOp, UpdateTextStyleOp } from "./canvas-op";
import {
  applyTextInlineRangePatches,
  clearTextInlineStyleKeys,
  isValidTextBaseFontSize,
  isValidTextInlineFontSize,
  nextTextStyleRevision,
  normalizeTextInlineStyles,
  rebaseTextInlineStyles,
  TEXT_INLINE_COLOR_PATTERN,
  TEXT_INLINE_STYLE_KEYS,
  TEXT_INLINE_STYLE_RANGE_PATCHES_MAX,
  TEXT_INLINE_STYLE_RUNS_MAX,
  type TextBaseStylePatch,
  type TextInlineRangePatch,
  type TextInlineStyle,
  type TextInlineStyleKey,
  type TextInlineStyleRange,
} from "./text-inline-style";

/**
 * 作成時にクライアント由来の `textStyleRevision` を落とす（保存は未設定＝0 から始める）。
 * revision はサーバだけが op の確定ごとに 1 進める競合境界であり、クライアント値を採用すると
 * 「+ 1 しても増えない大きな値」を持ち込まれて条件付き書き込みの排他が効かなくなる。
 */
export function stripTextStyleRevision(object: CanvasObject): CanvasObject {
  if (object.type !== "text" || object.textStyleRevision === undefined) return object;
  const { textStyleRevision: _ignoredRevision, ...rest } = object;
  return rest;
}

/** テキスト図形の基底書式（部分書式の差分計算の基準）。 */
export function textBaseStyleOf(object: TextCanvasObject): TextInlineStyle {
  return {
    ...(object.bold !== undefined ? { bold: object.bold } : {}),
    ...(object.color !== undefined ? { color: object.color } : {}),
    ...(object.fill !== undefined ? { fill: object.fill } : {}),
    ...(object.fontSize !== undefined ? { fontSize: object.fontSize } : {}),
  };
}

/** run 配列をオブジェクトへ載せる（空配列はフィールドごと未設定にする）。 */
function withInlineStyles(
  object: TextCanvasObject,
  runs: readonly TextInlineStyleRange[]
): TextCanvasObject {
  return runs.length > 0
    ? { ...object, inlineStyles: runs }
    : { ...object, inlineStyles: undefined };
}

export type TextStyleOpFailureCode = "invalid" | "conflict" | "limit";

/**
 * `textStyleRevision` の採番余地が尽きたときに利用者へ返す理由。
 * 本文更新・部分書式更新・全文書式による override 削除のどの経路でも同じ文面を使い、
 * 「この図形はもう更新できない」という同じ結果を同じ言葉で伝える。
 */
export const TEXT_STYLE_REVISION_EXHAUSTED_REASON =
  "このテキストは更新回数の上限に達したため、本文・書式を変更できません。";

export type TextStyleOpResult =
  | { readonly ok: true; readonly object: TextCanvasObject }
  | { readonly ok: false; readonly code: TextStyleOpFailureCode; readonly reason: string };

/** `object.text.style.update` の payload（BaseOp を除いた本体）。 */
export type TextStyleUpdateInput = Pick<
  UpdateTextStyleOp,
  "expectedText" | "baseStyle" | "rangePatches"
>;

/**
 * `object.text.style.update` を 1 オブジェクトへ原子的に適用する。
 *
 * 1. `expectedText` と現在の本文の完全一致を確認する（不一致は競合）。
 * 2. `textStyleRevision` の採番余地が残っていることを確認する（尽きていれば上限として拒否）。
 * 3. `baseStyle` を基底書式へ反映し、同名の inline override を全 run から削除する。
 * 4. `rangePatches` を順に適用し、正規形へ畳み直す。
 * 5. `textStyleRevision` を 1 進める。
 *
 * どれか 1 件でも不正なら op 全体を失敗として返し、途中結果を返さない。
 */
export function applyTextStyleUpdate(
  current: TextCanvasObject,
  input: TextStyleUpdateInput
): TextStyleOpResult {
  const baseKeys = presentBaseStyleKeys(input.baseStyle);
  const patches = input.rangePatches ?? [];
  if (baseKeys.length === 0 && patches.length === 0) {
    return {
      code: "invalid",
      ok: false,
      reason: "書式の変更内容が空のため適用できません。",
    };
  }
  if (patches.length > TEXT_INLINE_STYLE_RANGE_PATCHES_MAX) {
    return {
      code: "limit",
      ok: false,
      reason: `1 回の操作で変更できる範囲の件数（${TEXT_INLINE_STYLE_RANGE_PATCHES_MAX}件）を超えています。`,
    };
  }
  if (current.text !== input.expectedText) {
    return {
      code: "conflict",
      ok: false,
      reason: "本文が更新されたため、範囲を選び直してください。",
    };
  }
  const baseStyleError = validateBaseStyle(input.baseStyle);
  if (baseStyleError) return { code: "invalid", ok: false, reason: baseStyleError };
  const patchError = validateRangePatches(patches, current.text.length);
  if (patchError) return { code: "invalid", ok: false, reason: patchError };
  const nextRevision = nextTextStyleRevision(current.textStyleRevision);
  if (nextRevision === null) {
    return { code: "limit", ok: false, reason: TEXT_STYLE_REVISION_EXHAUSTED_REASON };
  }

  const nextBase: TextInlineStyle = { ...textBaseStyleOf(current), ...(input.baseStyle ?? {}) };
  let runs = current.inlineStyles ?? [];
  if (baseKeys.length > 0) {
    runs = clearTextInlineStyleKeys({
      base: nextBase,
      keys: baseKeys,
      runs,
      text: current.text,
    });
  }
  runs = applyTextInlineRangePatches({
    base: nextBase,
    patches,
    runs,
    text: current.text,
  });
  if (runs.length > TEXT_INLINE_STYLE_RUNS_MAX) {
    return {
      code: "limit",
      ok: false,
      reason: `1 つのテキストに設定できる部分書式の件数（${TEXT_INLINE_STYLE_RUNS_MAX}件）を超えています。範囲をまとめてお試しください。`,
    };
  }
  const withBase: TextCanvasObject = {
    ...current,
    ...(input.baseStyle?.bold !== undefined ? { bold: input.baseStyle.bold } : {}),
    ...(input.baseStyle?.color !== undefined ? { color: input.baseStyle.color } : {}),
    ...(input.baseStyle?.fill !== undefined ? { fill: input.baseStyle.fill } : {}),
    ...(input.baseStyle?.fontSize !== undefined ? { fontSize: input.baseStyle.fontSize } : {}),
    textStyleRevision: nextRevision,
  };
  return { object: withInlineStyles(withBase, runs), ok: true };
}

/** `object.text.update` の payload（BaseOp を除いた本文・書式部分）。 */
export type TextUpdateInput = Pick<
  UpdateTextOp,
  "text" | "inlineStyles" | "expectedTextStyleRevision"
>;

/**
 * `object.text.update` をテキスト図形へ適用する。
 *
 * - `inlineStyles` を伴う新クライアントの更新は、その run を権威として保存する
 *   （呼び出し側が `expectedTextStyleRevision` の一致を先に確認する）。
 * - `inlineStyles` を省略した旧クライアント由来の更新は、共有の単一置換差分で既存 run を
 *   rebase する（省略を「全部消す」と解釈しない）。
 *
 * どちらの経路でも `textStyleRevision` を 1 進める。採番余地が尽きている場合は本文を変えず
 * null を返す（呼び出し側が更新を拒否する）。値を進められないまま本文だけを保存すると、
 * 保存済み run と本文の対応を古い revision の更新で壊せてしまう。
 */
export function applyTextUpdateToTextObject(
  current: TextCanvasObject,
  input: TextUpdateInput
): TextCanvasObject | null {
  const nextRevision = nextTextStyleRevision(current.textStyleRevision);
  if (nextRevision === null) return null;
  const base = textBaseStyleOf(current);
  const runs =
    input.inlineStyles !== undefined
      ? normalizeTextInlineStyles({ base, runs: input.inlineStyles, text: input.text })
      : rebaseTextInlineStyles({
          base,
          nextText: input.text,
          prevText: current.text,
          runs: current.inlineStyles,
        });
  return withInlineStyles({ ...current, text: input.text, textStyleRevision: nextRevision }, runs);
}

/**
 * 全文スタイル op（`object.style.update`）がテキストへ適用されるときに、同名の inline override を
 * 削除する。旧クライアント互換経路と、REST / MCP の style patch の両方で共有する。
 *
 * override を消したときは `textStyleRevision` も 1 進める。進めないと、この削除より前の
 * revision を編集元として持っている新クライアントの本文更新（`inlineStyles` 付き）が
 * revision 一致で通ってしまい、削除済みの override を古い run で復活させられる。
 * 判定は「実際に削除処理を行ったか」だけで決まるため、サーバの op 適用とクライアントの
 * 楽観適用は同じ結果へ収束する。
 *
 * `textStyleRevision` の採番余地が尽きている場合は、override を消さず null を返す
 * （呼び出し側が更新を拒否する）。進めずに消すと、削除より前の revision を編集元に持つ
 * 本文更新が revision 一致で通り、消したはずの override が復活する。
 * 判定は run の有無より先に行う。run が無ければ revision は進めない契約だが、run の有無で
 * 「上限に達したテキストでも保存できる / できない」が分かれると、同じ op の可否が対象の状態に
 * よって変わり、上限到達で更新を拒否するという契約に抜け道ができる。
 * 部分書式を持たない項目（文字寄せ）は `keys` が空になるため、この判定の対象外である。
 */
export function clearTextInlineOverridesForBaseKeys(
  object: TextCanvasObject,
  keys: readonly TextInlineStyleKey[]
): TextCanvasObject | null {
  if (keys.length === 0) return object;
  const nextRevision = nextTextStyleRevision(object.textStyleRevision);
  if (nextRevision === null) return null;
  if ((object.inlineStyles?.length ?? 0) === 0) return object;
  const runs = clearTextInlineStyleKeys({
    base: textBaseStyleOf(object),
    keys,
    runs: object.inlineStyles,
    text: object.text,
  });
  return withInlineStyles({ ...object, textStyleRevision: nextRevision }, runs);
}

//------------------------------------------------------------------------------
// 検証
//------------------------------------------------------------------------------

/** `baseStyle` に実際に含まれている項目キー。 */
export function presentBaseStyleKeys(
  baseStyle: TextBaseStylePatch | undefined
): readonly TextInlineStyleKey[] {
  if (!baseStyle) return [];
  return TEXT_INLINE_STYLE_KEYS.filter((key) => baseStyle[key] !== undefined);
}

function validateBaseStyle(baseStyle: TextBaseStylePatch | undefined): string | null {
  if (!baseStyle) return null;
  if (baseStyle.color !== undefined && !TEXT_INLINE_COLOR_PATTERN.test(baseStyle.color)) {
    return "文字色の形式が不正です。";
  }
  // 全文の背景色は「背景なし」を透明表現で保存するため、hex 以外も受け付ける
  // （描画側が hex だけを採用する既存契約を維持する）。
  if (baseStyle.fill !== undefined && typeof baseStyle.fill !== "string") {
    return "背景色の形式が不正です。";
  }
  if (baseStyle.fontSize !== undefined && !isValidTextBaseFontSize(baseStyle.fontSize)) {
    return "文字サイズが範囲外です。";
  }
  if (baseStyle.bold !== undefined && typeof baseStyle.bold !== "boolean") {
    return "太字の指定が不正です。";
  }
  return null;
}

function validateRangePatches(
  patches: readonly TextInlineRangePatch[],
  textLength: number
): string | null {
  for (const patch of patches) {
    if (!Number.isInteger(patch.start) || !Number.isInteger(patch.end)) {
      return "範囲の指定が不正です。";
    }
    if (patch.start < 0 || patch.end > textLength || patch.start >= patch.end) {
      return "範囲の指定が本文の範囲外です。";
    }
    const style = patch.style;
    const hasKey = TEXT_INLINE_STYLE_KEYS.some((key) => style[key] !== undefined);
    if (!hasKey) return "範囲の書式指定が空です。";
    if (
      style.color !== undefined &&
      style.color !== null &&
      !TEXT_INLINE_COLOR_PATTERN.test(style.color)
    ) {
      return "文字色の形式が不正です。";
    }
    if (
      style.fill !== undefined &&
      style.fill !== null &&
      !TEXT_INLINE_COLOR_PATTERN.test(style.fill)
    ) {
      return "背景色の形式が不正です。";
    }
    if (
      style.fontSize !== undefined &&
      style.fontSize !== null &&
      !isValidTextInlineFontSize(style.fontSize)
    ) {
      return "文字サイズが範囲外です。";
    }
    if (style.bold !== undefined && style.bold !== null && typeof style.bold !== "boolean") {
      return "太字の指定が不正です。";
    }
  }
  return null;
}
